import React from "react";
import { motion } from "framer-motion";
import {
    ShieldCheck,
    Zap,
    LineChart,
    Wallet,
    Globe2,
    Headphones,
    Lock,
    Layers,
} from "lucide-react";
import SectionHeading from "./SectionHeading";
import { staggerVariants, VIEWPORT_ONCE_TIGHT } from "../lib/motion";

const features = [
    {
        icon: Zap,
        title: "Tight Spreads",
        copy: "Raw pricing from deep liquidity. Major FX pairs from 0.6 pips, BTC/USD from 18 USD.",
    },
    {
        icon: LineChart,
        title: "MT5 Trading Engine",
        copy: "21 timeframes, 38 indicators, EAs, depth-of-market and one-click execution.",
    },
    {
        icon: ShieldCheck,
        title: "Anjouan Regulated",
        copy: "XPO Markets Ltd is registered with the Registrar of International Business Companies, Anjouan, Comoros (Co. No. 16297).",
    },
    {
        icon: Wallet,
        title: "Negative Balance Protection",
        copy: "You can never lose more than your account balance. Standard on every retail account.",
    },
    {
        icon: Globe2,
        title: "Forex & Crypto",
        copy: "Major, minor, and exotic FX pairs plus the most liquid crypto CFDs.",
    },
    {
        icon: Layers,
        title: "Up to 1:500 Leverage",
        copy: "Flexible leverage to suit your strategy and risk profile. Adjustable per account.",
    },
    {
        icon: Headphones,
        title: "24/5 Support",
        copy: "Real human traders on chat and email — no bots, no scripts.",
    },
    {
        icon: Lock,
        title: "Tier-1 Banking",
        copy: "Client funds held with reputable banking partners and reconciled daily.",
    },
];

const cardVariants = staggerVariants({ y: 20, duration: 0.5, step: 0.05 });

export default function WhyXpo() {
    return (
        <section
            data-testid="why-xpo-section"
            className="relative py-24 lg:py-32"
        >
            <div className="xpo-container">
                <SectionHeading
                    overline="Why XPO Markets"
                    title="Built for traders who measure twice and click once."
                    description="Eight reasons serious traders choose XPO Markets — from execution quality to fund safety, every detail is engineered around your edge."
                />

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
                    {features.map((f, i) => (
                        <motion.div
                            key={f.title}
                            data-testid={`feature-card-${i}`}
                            variants={cardVariants}
                            initial="hidden"
                            whileInView="visible"
                            custom={i}
                            viewport={VIEWPORT_ONCE_TIGHT}
                            className="xpo-glass p-7 group"
                        >
                            <div className="w-11 h-11 rounded-xl bg-[#A6E3DC]/10 border border-[#A6E3DC]/20 grid place-items-center mb-5 group-hover:bg-[#A6E3DC]/20 transition">
                                <f.icon size={20} className="text-[#A6E3DC]" />
                            </div>
                            <h3 className="text-lg font-bold text-white tracking-tight">
                                {f.title}
                            </h3>
                            <p className="text-slate-400 text-sm mt-2 leading-relaxed">
                                {f.copy}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
